import { useEffect, useState, type FormEvent } from 'react';
import { Plus } from 'lucide-react';
import { useApiKeys, useCreateApiKey, useRevokeApiKey } from '@/api/hooks';
import { errorMessage } from '@/api/client';
import type { ApiKeyCreated, ApiKeyOut, ApiScope } from '@/api/types';
import { PageHeading } from '@/app/PageHeading';
import { Alert } from '@/components/arc/alert/alert';
import { Badge } from '@/components/arc/badge/badge';
import { Button } from '@/components/arc/button/button';
import { Checkbox } from '@/components/arc/checkbox/checkbox';
import { CodeBlock } from '@/components/arc/code-block/code-block';
import { ConfirmMorph } from '@/components/arc/confirm-morph/confirm-morph';
import { CopyButton } from '@/components/arc/copy-button/copy-button';
import { Dialog, DialogContent } from '@/components/arc/dialog/dialog';
import { EmptyState } from '@/components/arc/empty-state/empty-state';
import { Input } from '@/components/arc/input/input';
import { Skeleton } from '@/components/arc/skeleton/skeleton';
import { SortableDataTable, type DataColumn } from '@/components/arc/sortable-data-table/sortable-data-table';
import { fmtDate, fmtRelative } from '@/lib/format';
import { useMoment, useTimezone } from '@/module/MomentProvider';
import styles from './api-keys.module.css';

type KeyRow = { [K in keyof ApiKeyOut]: ApiKeyOut[K] } & { last_used: string; state: string };

const SCOPES: { value: ApiScope; label: string; description: string }[] = [
  { value: 'read', label: 'Read', description: 'List and fetch videos, moments, segments and campaigns' },
  {
    value: 'write',
    label: 'Write',
    description: 'Create and change sources, segments, creatives and campaigns',
  },
  { value: 'export', label: 'Export', description: 'Start exports and download their files' },
  { value: 'sdk', label: 'SDK', description: 'Fetch overlay manifests and report SDK events' },
];

export function ApiKeysPage() {
  const tz = useTimezone();
  const { client } = useMoment();
  const keys = useApiKeys();
  const revoke = useRevokeApiKey();
  const [open, setOpen] = useState(false);
  const [run, setRun] = useState(0);
  // The origin is browser-only, so the usage example fills it in after the first render.
  const [origin, setOrigin] = useState('');
  useEffect(() => setOrigin(window.location.origin), []);

  const items = keys.data ?? [];
  const rows: KeyRow[] = items.map((k) => ({
    ...k,
    last_used: k.last_used_at ?? '',
    state: k.revoked_at ? 'revoked' : 'active',
  }));
  const isEmpty = !keys.isLoading && !keys.isError && rows.length === 0;
  const apiBase = /^https?:\/\//i.test(client.baseUrl)
    ? client.baseUrl
    : `${origin || 'https://your-panel.example'}${client.baseUrl}`;
  const usage = `curl -H "X-API-Key: mk_…" ${apiBase}/videos`;

  const openDialog = () => {
    setRun((n) => n + 1);
    setOpen(true);
  };

  const columns: DataColumn<KeyRow>[] = [
    {
      key: 'name',
      label: 'Name',
      render: (_, k) => (
        <div className={styles.cell}>
          <span>{k.name}</span>
          <span className={styles.cellSub}>
            <code className={styles.mono}>{k.key_prefix}…</code>
          </span>
        </div>
      ),
    },
    {
      key: 'scopes',
      label: 'Scopes',
      sortable: false,
      render: (_, k) => (
        <span className={styles.scopes}>
          {k.scopes.map((s) => (
            <Badge key={s} size="sm">
              {s}
            </Badge>
          ))}
        </span>
      ),
    },
    {
      key: 'state',
      label: 'Status',
      render: (_, k) =>
        k.revoked_at ? (
          <Badge size="sm">Revoked</Badge>
        ) : (
          <Badge size="sm" tone="success">
            Active
          </Badge>
        ),
    },
    { key: 'created_at', label: 'Created', render: (v) => fmtDate(String(v), tz) },
    {
      key: 'last_used',
      label: 'Last used',
      render: (v) => (v ? fmtRelative(String(v)) : <span className="text-muted">Never</span>),
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (_, k) =>
        k.revoked_at ? null : (
          <span className={styles.rowActions}>
            <ConfirmMorph
              label="Revoke"
              prompt={`Revoke ${k.name}?`}
              confirmLabel="Revoke"
              pendingLabel="Revoking"
              doneLabel="Revoked"
              errorLabel="Could not revoke"
              onConfirm={() => revoke.mutateAsync(k.id)}
            />
          </span>
        ),
    },
  ];

  return (
    <main className="page">
      <PageHeading
        title="API keys"
        description="Keys for the Scenewise REST API. Scopes limit what each key can do."
        actions={
          !isEmpty ? (
            <Button onClick={openDialog}>
              <Plus size={16} strokeWidth={1.75} aria-hidden="true" /> Create key
            </Button>
          ) : undefined
        }
      />

      {keys.isLoading ? (
        <Skeleton lines={4} label="Loading API keys" />
      ) : keys.isError ? (
        <Alert tone="danger" title="API keys did not load">
          {errorMessage(keys.error)}{' '}
          <Button variant="secondary" size="sm" onClick={() => void keys.refetch()}>
            Try again
          </Button>
        </Alert>
      ) : isEmpty ? (
        <EmptyState
          title="No API keys yet"
          description="Create a key to call the API from your own systems. The full key is shown once."
          action={
            <Button onClick={openDialog}>
              <Plus size={16} strokeWidth={1.75} aria-hidden="true" /> Create key
            </Button>
          }
        />
      ) : (
        <div className="table-scroll">
          <SortableDataTable<KeyRow>
            rows={rows}
            columns={columns}
            rowKey="id"
            caption="API keys"
            defaultSort={{ key: 'created_at', direction: 'desc' }}
            itemName={{ one: 'key', other: 'keys' }}
          />
        </div>
      )}

      <section className={styles.usage} aria-labelledby="usage-heading">
        <div className={styles.cell}>
          <h2 id="usage-heading" className={styles.sectionTitle}>
            Using a key
          </h2>
          <span className="text-secondary">Send the key in the X-API-Key header on every request.</span>
        </div>
        <CodeBlock code={usage} language="bash" filename="example.sh" />
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent title="Create API key" description="Name the key after the system that will use it.">
          <CreateKeyForm key={run} onDone={() => setOpen(false)} />
        </DialogContent>
      </Dialog>
    </main>
  );
}

function CreateKeyForm({ onDone }: { onDone: () => void }) {
  const create = useCreateApiKey();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<ApiScope[]>(['read']);
  const [created, setCreated] = useState<ApiKeyCreated | null>(null);
  const valid = name.trim() !== '' && scopes.length > 0;

  const toggle = (scope: ApiScope, on: boolean) =>
    setScopes((prev) => (on ? [...new Set([...prev, scope])] : prev.filter((s) => s !== scope)));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid || create.isPending) return;
    try {
      setCreated(await create.mutateAsync({ name: name.trim(), scopes }));
    } catch {
      /* shown in the alert below */
    }
  };

  if (created) {
    return (
      <div className={styles.form}>
        <Alert tone="warning" title="Copy the key now">
          It is shown only this once. Scenewise stores a hash and cannot show it again.
        </Alert>
        <div className={styles.secret}>
          <code>{created.key}</code>
          <CopyButton value={created.key} label="Copy key" />
        </div>
        <p className="text-secondary">
          {created.name}: {created.scopes.join(', ')}
        </p>
        <div className={styles.footer}>
          <Button onClick={onDone}>Done</Button>
        </div>
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={(e) => void submit(e)}>
      <Input
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Data warehouse sync"
        required
        autoFocus
      />
      <div className={styles.scopeGroup} role="group" aria-labelledby="scopes-label">
        <span id="scopes-label" className={styles.scopeLabel}>
          Scopes
        </span>
        {SCOPES.map((s) => (
          <Checkbox
            key={s.value}
            label={s.label}
            description={s.description}
            checked={scopes.includes(s.value)}
            onCheckedChange={(v) => toggle(s.value, v === true)}
          />
        ))}
      </div>
      {create.error && (
        <Alert tone="danger" title="Could not create the key">
          {errorMessage(create.error)}
        </Alert>
      )}
      <div className={styles.footer}>
        <Button type="button" variant="secondary" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" loading={create.isPending} disabled={!valid}>
          Create key
        </Button>
      </div>
    </form>
  );
}
